import { isString } from "es-toolkit";
import { type CompiledCells } from "packages/state/src/internal/runtime/cell-compiler.atom";
import type { EditorState } from "prosemirror-state";

import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";
import { type CellCompilerFn, registerCellCompiler as _registerCellCompiler } from "@moment/state";

import {
	type Cell,
	type CellInfo,
	type CellMap,
	equals,
	proseMirrorEditorStateToCells,
} from "~/models/cell";

import { emitCell } from "./emit";

const logger = new Logger("cell-compiler-registration");

/**
 * Compile a single cell using emitCell.
 *
 * This function was originally moved from the `page.machine.ts` file, if you need to look up its
 * history in version control.
 */
async function compileCell(cell: Cell): Promise<CellInfo> {
	const env = process.env["APP_ENV"] as MomentAppEnv | undefined;

	try {
		const emitInfo = await emitCell(cell, {}, env);

		if (emitInfo.kind === "StaticError") {
			return {
				cell,
				status: "error",
				message: emitInfo.message,
			};
		}

		return {
			cell,
			emitInfo,
			status: "executable",
		};
	} catch (error) {
		return {
			cell,
			status: "error",
			message: error instanceof Error ? error.message : "Unknown error",
		};
	}
}

/**
 * Finds and compiles all compute cells in some `EditorState` that have not already been compiled.
 * The main point of this function is to be registered the state package's `registerCellCompiler`
 * function, so that the compiler `atomEffect` can compile cells as necessary.
 *
 * This function was originally moved from the `page.machine.ts` file, if you need to look up its
 * history in version control.
 */
const cellCompiler: CellCompilerFn<CellInfo> = async (
	editorState: EditorState,
	previousResult: CompiledCells<CellInfo>
): Promise<CompiledCells<CellInfo>> => {
	// Default to empty object just to be safe. (The harness should return `{}` by default.)
	const previousCells = previousResult ?? {};

	// Extract only compute_cell nodes from the document
	const nextCells = proseMirrorEditorStateToCells(
		editorState,
		(node) => node.type === schema.nodes.compute_cell
	);

	const nextCellIDs = new Set(nextCells.map((cell) => cell.id).filter(isString));

	// Find cells that have changed (need recompilation)
	const cellsToCompile = nextCells.filter((cell) => {
		const previousCell = previousCells?.[cell?.id];
		if (!previousCell) return true; // Is an entirely new cell.
		return !equals(cell, previousCell.cell); // Was a previous cell that changed.
	});

	// Check if any cells were deleted
	const previousCellIDs = new Set(Object.keys(previousCells));
	const anyCellsDeleted = previousCellIDs.difference(nextCellIDs).size > 0;

	// Early return if nothing changed
	if (!anyCellsDeleted && cellsToCompile.length === 0) {
		return previousCells;
	}

	// Compile changed cells in parallel
	const newCompiledCells = Object.fromEntries(
		await Promise.all(
			cellsToCompile.map(async (cell) => {
				return [cell.id, await compileCell(cell)] as const;
			})
		)
	);

	// Merge: start fresh with only cells that still exist
	const compiledCells: CellMap = {};

	for (const id of nextCellIDs) {
		// Use newly compiled cell if available
		const newlyCompiledCell = newCompiledCells[id];
		if (newlyCompiledCell) {
			compiledCells[id] = newlyCompiledCell;
			continue;
		}

		// Otherwise use the previous cell (unchanged)
		const previousCell = previousCells[id];
		if (previousCell) {
			compiledCells[id] = previousCell;
			continue;
		}
	}

	logger.debug("Compilation complete", {
		total: Object.keys(compiledCells).length,
		compiled: cellsToCompile.length,
		deleted: anyCellsDeleted,
	});

	return compiledCells;
};

/**
 * Register the cell compiler with the state package. Call this once during app initialization.
 */
export const registerCellCompiler = (): void => {
	_registerCellCompiler(cellCompiler);
};
